import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { format, startOfDay, endOfDay } from 'date-fns';
import { fetchDetailedConversion, fetchProductFunnels } from './analyticsApi';
import { useErrorSetter } from '../hooks/useUiMessages';
import styles from './Analytics.module.css';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f'];

const ConversionFunnel = ({ dateRange }) => {
  const setError = useErrorSetter();
  const [loading, setLoading] = useState(true);
  const [conversionData, setConversionData] = useState({
    funnelSteps: [],
    conversionRates: [],
    dropOffPoints: [],
    cohortAnalysis: [],
    deviceConversion: [],
    sourceConversion: [],
    timeToConversion: [],
  productFunnels: { range: {}, resume: {}, coverLetter: {}, interview: {}, jobs: {} },
    summary: {
      totalVisitors: 0,
      totalConversions: 0,
      overallConversionRate: 0,
      averageTimeToConversion: 0,
      topDropOffStage: '',
      bestPerformingSource: ''
    }
  });

  const fetchConversionData = useCallback(async () => {
    try {
      setLoading(true);

      const token = sessionStorage.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
      if (!token) {
        setConversionData({
          funnelSteps: [], conversionRates: [], dropOffPoints: [], cohortAnalysis: [], deviceConversion: [], sourceConversion: [], timeToConversion: [], summary: { totalVisitors:0,totalConversions:0,overallConversionRate:0,averageTimeToConversion:0,topDropOffStage:'',bestPerformingSource:'' }
        });
        return;
      }

      const startDate = startOfDay(dateRange.start).toISOString();
      const endDate = endOfDay(dateRange.end).toISOString();
      const [raw, productFunnels] = await Promise.all([
        fetchDetailedConversion(startDate, endDate),
        fetchProductFunnels(startDate, endDate)
      ]);

      const funnelSteps = processFunnelSteps(raw);
      const dropOffPoints = processDropOffPoints(raw);
      const deviceConversion = Array.isArray(raw.deviceConversion) ? raw.deviceConversion.map((d, index) => ({ device: (d.device || 'Unknown').toString().toLowerCase(), conversionRate: d.rate || 0, conversions: d.conversions || 0, visitors: d.sessions || 0, color: COLORS[index % COLORS.length] })) : [];
      const sourceConversion = Array.isArray(raw.sourceConversion) ? raw.sourceConversion.map(s => ({ source: s.source || 'Direct', conversionRate: s.rate || 0, conversions: s.conversions || 0, visitors: s.sessions || 0 })) : [];
      const timeToConversion = Array.isArray(raw.timeToConversion) ? raw.timeToConversion.map(t => ({ timeRange: t.bucket, count: t.count, percentage: 0 })) : [];

      // Best performing source (highest rate with minimum traffic of 5 sessions)
      const bestSource = sourceConversion
        .filter(s => (s.visitors || 0) >= 5)
        .sort((a,b) => (b.conversionRate || 0) - (a.conversionRate || 0))[0]?.source || '';
      // Top dropoff stage by highest drop-off rate
      const topDrop = dropOffPoints.slice().sort((a,b)=> (b.dropOffRate||0) - (a.dropOffRate||0))[0]?.stage || '';

      setConversionData({
        funnelSteps,
        conversionRates: processConversionRates(raw),
        dropOffPoints,
        cohortAnalysis: processCohortAnalysis(raw),
        deviceConversion,
        sourceConversion,
        timeToConversion,
        productFunnels,
        summary: {
          totalVisitors: raw.pageViews || 0,
          totalConversions: raw.conversions || 0,
          overallConversionRate: raw.overallConversionRate || 0,
          averageTimeToConversion: 0,
          topDropOffStage: topDrop,
          bestPerformingSource: bestSource
        }
      });

    } catch (err) {
      console.error('Error fetching conversion data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, setError]);

  // Removed mock generator – real data now used (safe fallbacks handled).

  useEffect(() => {
    fetchConversionData();
  }, [fetchConversionData]);

  const processFunnelSteps = (data) => {
    const defaultSteps = [
      { name: 'Page Views', value: data.pageViews || 0, color: COLORS[0] },
      { name: 'Started Chat', value: data.chatStarts || 0, color: COLORS[1] },
      { name: 'Sent Messages', value: data.messagesSent || 0, color: COLORS[2] },
  { name: 'Engagement', value: data.deepEngagement || 0, color: COLORS[3] },
  { name: 'Registrations', value: data.registrations || 0, color: '#4caf50' },
  { name: 'Paid Conversions', value: data.conversions || 0, color: COLORS[4] }
    ];

    return defaultSteps.map((step, index) => ({
      ...step,
      percentage: index === 0 ? 100 : ((step.value / defaultSteps[0].value) * 100).toFixed(1)
    }));
  };

  // Build service-specific funnel steps from productFunnels
  const buildServiceFunnels = () => {
    const pf = conversionData.productFunnels || {};
    const pct = (num, denom) => (denom ? ((num / denom) * 100).toFixed(1) : '0.0');

    const resume = pf.resume || {};
    const resumeSteps = [
      { name: 'Resume Created', value: resume.created || 0, color: '#1f77b4' },
      { name: 'Optimized', value: resume.optimized || 0, color: '#17becf' },
      { name: 'Downloaded', value: resume.downloaded || 0, color: '#2ca02c' },
    ];
    resumeSteps.forEach((s, i) => (s.percentage = i === 0 ? 100 : pct(s.value, resumeSteps[0].value)));

    // Additional summary funnel: Resume Generated (created + optimized) → downloaded
    const resumeGeneratedTotal = (resume.created || 0) + (resume.optimized || 0);
    const resumeGeneratedSteps = [
      { name: 'Generated', value: resumeGeneratedTotal, color: '#1f77b4' },
      { name: 'Downloaded', value: resume.downloaded || 0, color: '#2ca02c' },
    ];
    resumeGeneratedSteps.forEach((s, i) => (s.percentage = i === 0 ? 100 : pct(s.value, resumeGeneratedSteps[0].value)));

    const cover = pf.coverLetter || {};
    const coverSteps = [
      { name: 'Cover Created', value: cover.created || 0, color: '#ff7f0e' },
      { name: 'Downloaded', value: cover.downloaded || 0, color: '#9467bd' },
    ];
    coverSteps.forEach((s, i) => (s.percentage = i === 0 ? 100 : pct(s.value, coverSteps[0].value)));

    const interview = pf.interview || {};
    const interviewSteps = [
      { name: 'Entry', value: interview.entry || 0, color: '#8c564b' },
      { name: 'Requested', value: interview.requested || 0, color: '#e377c2' },
      { name: 'Generated', value: interview.generated || 0, color: '#7f7f7f' },
    ];
    interviewSteps.forEach((s, i) => (s.percentage = i === 0 ? 100 : pct(s.value, interviewSteps[0].value)));

    const jobs = pf.jobs || {};
    const jobsSteps = [
      { name: 'Fetch Started', value: jobs.started || 0, color: '#bcbd22' },
      { name: 'Fetch Completed', value: jobs.completed || 0, color: '#17becf' },
    ];
    jobsSteps.forEach((s, i) => (s.percentage = i === 0 ? 100 : pct(s.value, jobsSteps[0].value)));

    return [
      { title: 'Resume Funnel', steps: resumeSteps },
      { title: 'Cover Letter Funnel', steps: coverSteps },
      { title: 'Interview Funnel', steps: interviewSteps },
  { title: 'Resume Generated', steps: resumeGeneratedSteps },
  { title: 'Jobs Fetched', steps: jobsSteps },
    ];
  };

  const processConversionRates = (data) => {
    if (!data.dailyConversion) return [];
    
    return data.dailyConversion.map(item => ({
      date: format(new Date(item.date), 'MMM dd'),
      conversionRate: parseFloat(item.rate) || 0,
      conversions: item.conversions || 0,
      visitors: item.visitors || 0
    }));
  };

  const processDropOffPoints = (data) => {
    const dropOffs = data.dropOffAnalysis || {};
    
    return Object.entries(dropOffs).map(([stage, dropOff]) => ({
      stage,
      dropOffRate: parseFloat(dropOff.rate) || 0,
      dropOffCount: dropOff.count || 0,
      previousStageCount: dropOff.previousCount || 0
    }));
  };

  const processCohortAnalysis = (data) => {
    if (!data.cohortData) return [];
    
    return data.cohortData.map(cohort => ({
      week: `Week ${cohort.week}`,
      signups: cohort.signups || 0,
      conversions: cohort.conversions || 0,
      conversionRate: cohort.conversionRate || 0
    }));
  };


  // helpers inlined in fetchConversionData for device/source/timeToConversion

  const formatPercentage = (value) => {
    return `${parseFloat(value).toFixed(1)}%`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)}m`;
    if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
    return `${Math.round(minutes / 1440)}d`;
  };

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading conversion data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className={styles.conversionContainer}>
      {/* Summary Cards */}
      <Grid container spacing={3} className={styles.metricsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                Total Visitors
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {formatNumber(conversionData.summary.totalVisitors)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                Total Conversions
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {formatNumber(conversionData.summary.totalConversions)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                Conversion Rate
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {formatPercentage(conversionData.summary.overallConversionRate)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                Avg. Time to Convert
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {formatDuration(conversionData.summary.averageTimeToConversion)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Grid */}
      <Grid container spacing={3} className={styles.chartsGrid}>
  {/* Conversion Funnel (Site-wide) */}
        <Grid item xs={12} lg={8}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Conversion Funnel
              </Typography>
              <Box className={styles.funnelContainer}>
                {conversionData.funnelSteps.map((step, index) => (
                  <Box key={index} className={styles.funnelStep}>
                    <Box className={styles.funnelStepHeader}>
                      <Typography variant="subtitle1" className={styles.funnelStepName}>
                        {step.name}
                      </Typography>
                      <Typography variant="h6" className={styles.funnelStepValue}>
                        {formatNumber(step.value)} ({step.percentage}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(step.percentage)}
                      className={styles.funnelProgress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: step.color
                        }
                      }}
                    />
                    {index < conversionData.funnelSteps.length - 1 && (
                      <Typography variant="caption" className={styles.funnelDropOff}>
                        {((conversionData.funnelSteps[index].value - conversionData.funnelSteps[index + 1].value) / (conversionData.funnelSteps[index].value || 1) * 100).toFixed(1)}% drop-off
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Service Funnels */}
        <Grid item xs={12} lg={4}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Service Funnels
              </Typography>
              <Box className={styles.funnelContainer} sx={{ pr: 1, pb: 1 }}>
                {buildServiceFunnels().map((svc, idx) => (
                  <Box key={idx} className={styles.funnelStep} sx={{ mb: 2 }}>
                    <Box className={styles.funnelStepHeader}>
                      <Typography variant="subtitle1" className={styles.funnelStepName}>
                        {svc.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {svc.steps[0]?.value ? `${formatNumber(svc.steps[svc.steps.length - 1].value)} of ${formatNumber(svc.steps[0].value)}` : '0 of 0'}
                      </Typography>
                    </Box>
                    {svc.steps.map((step, sIdx) => (
                      <Box key={sIdx} sx={{ mb: 1 }}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="caption">{step.name}</Typography>
                          <Typography variant="caption">{formatNumber(step.value)} ({step.percentage}%)</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={parseFloat(step.percentage)}
                          className={styles.funnelProgress}
                          sx={{ height: 6, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: step.color } }}
                        />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Device Conversion */}
        <Grid item xs={12}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Conversion by Device
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={conversionData.deviceConversion}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="conversionRate"
                    label={({ device, conversionRate }) => `${device}: ${conversionRate.toFixed(1)}%`}
                  >
                    {conversionData.deviceConversion.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Conversion Rate']} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Daily Conversion Trends */}
        <Grid item xs={12}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Daily Conversion Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={conversionData.conversionRates}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'conversionRate' ? `${value}%` : value,
                      name === 'conversionRate' ? 'Conversion Rate' : name
                    ]}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="conversionRate" 
                    stroke={COLORS[0]} 
                    strokeWidth={3}
                    name="Conversion Rate (%)"
                  />
                  <Bar dataKey="conversions" fill={COLORS[1]} name="Conversions" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Drop-off Analysis Table */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Drop-off Analysis
              </Typography>
              <TableContainer component={Paper} className={styles.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Stage</TableCell>
                      <TableCell align="right">Drop-off Rate</TableCell>
                      <TableCell align="right">Users Lost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conversionData.dropOffPoints.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.stage}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={formatPercentage(row.dropOffRate)}
                            color={row.dropOffRate > 50 ? 'error' : row.dropOffRate > 25 ? 'warning' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.dropOffCount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Source Performance */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Traffic Source Conversion
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={conversionData.sourceConversion}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="source" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    name === 'conversionRate' ? `${value}%` : value,
                    name === 'conversionRate' ? 'Conversion Rate' : 'Conversions'
                  ]} />
                  <Bar dataKey="conversionRate" fill={COLORS[2]} name="Conversion Rate (%)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default ConversionFunnel;
